/**
 * Viewer Templates System
 * 
 * Each template defines the layout and positioning of UI elements in the viewer.
 * Templates can be selected per-tour or as a default in branding settings.
 */

export interface ViewerTemplate {
  id: string
  name: string
  description: string
  /** Scene list position */
  sceneListPosition: 'bottom' | 'right' | 'left' | 'hidden'
  /** Scene list style */
  sceneListStyle: 'carousel' | 'grid' | 'list' | 'thumbnails'
  /** Whether scene list can be collapsed */
  sceneListCollapsible: boolean
  /** Default collapsed state */
  sceneListDefaultCollapsed: boolean
  /** Controls position */
  controlsPosition: 'bottom-center' | 'bottom-left' | 'bottom-right' | 'top-center' | 'top-right'
  /** Show scene title overlay */
  showSceneTitle: boolean
  /** Scene title position */
  sceneTitlePosition: 'top-center' | 'top-left' | 'bottom-left'
  /** Show scene counter (1/9) */
  showSceneCounter: boolean
  /** Show navigation arrows for prev/next scene */
  showNavArrows: boolean
  /** Thumbnail size: small (80px), medium (120px), large (160px) */
  thumbnailSize: 'small' | 'medium' | 'large'
  /** Controls style */
  controlsStyle: 'rounded' | 'pill' | 'flat' | 'minimal'
  /** Overall dark/light mode */
  uiMode: 'dark' | 'light' | 'auto'
  /** Transition effect between scenes */
  transitionEffect: 'fade' | 'slide' | 'zoom' | 'none'
}

export const templates: Record<string, ViewerTemplate> = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional layout with scene list on the right sidebar',
    sceneListPosition: 'right',
    sceneListStyle: 'list',
    sceneListCollapsible: true,
    sceneListDefaultCollapsed: false,
    controlsPosition: 'bottom-center',
    showSceneTitle: true,
    sceneTitlePosition: 'top-left',
    showSceneCounter: true,
    showNavArrows: true,
    thumbnailSize: 'medium',
    controlsStyle: 'rounded',
    uiMode: 'dark',
    transitionEffect: 'fade',
  },
  carousel: {
    id: 'carousel',
    name: 'Carousel',
    description: 'Scene thumbnails as a bottom carousel strip',
    sceneListPosition: 'bottom',
    sceneListStyle: 'carousel',
    sceneListCollapsible: true,
    sceneListDefaultCollapsed: false,
    controlsPosition: 'bottom-right',
    showSceneTitle: true,
    sceneTitlePosition: 'top-center',
    showSceneCounter: true,
    showNavArrows: true,
    thumbnailSize: 'medium',
    controlsStyle: 'pill',
    uiMode: 'dark',
    transitionEffect: 'fade',
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean, distraction-free viewing with hidden controls',
    sceneListPosition: 'hidden',
    sceneListStyle: 'thumbnails',
    sceneListCollapsible: true,
    sceneListDefaultCollapsed: true,
    controlsPosition: 'bottom-right',
    showSceneTitle: false,
    sceneTitlePosition: 'top-center',
    showSceneCounter: false,
    showNavArrows: true,
    thumbnailSize: 'small',
    controlsStyle: 'minimal',
    uiMode: 'dark',
    transitionEffect: 'fade',
  },
  gallery: {
    id: 'gallery',
    name: 'Gallery',
    description: 'Large thumbnails at the bottom with scene info overlay',
    sceneListPosition: 'bottom',
    sceneListStyle: 'carousel',
    sceneListCollapsible: true,
    sceneListDefaultCollapsed: false,
    controlsPosition: 'top-right',
    showSceneTitle: true,
    sceneTitlePosition: 'bottom-left',
    showSceneCounter: true,
    showNavArrows: true,
    thumbnailSize: 'large',
    controlsStyle: 'rounded',
    uiMode: 'dark',
    transitionEffect: 'slide',
  },
  sidebar: {
    id: 'sidebar',
    name: 'Sidebar',
    description: 'Left sidebar with scene list and details panel',
    sceneListPosition: 'left',
    sceneListStyle: 'list',
    sceneListCollapsible: true,
    sceneListDefaultCollapsed: false,
    controlsPosition: 'bottom-center',
    showSceneTitle: true,
    sceneTitlePosition: 'top-center',
    showSceneCounter: true,
    showNavArrows: false,
    thumbnailSize: 'medium',
    controlsStyle: 'rounded',
    uiMode: 'dark',
    transitionEffect: 'fade',
  },
}

export function getTemplate(id: string): ViewerTemplate {
  return templates[id] || templates.classic
}

export function getTemplateList(): ViewerTemplate[] {
  return Object.values(templates)
}
